import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { AllowedLanguages } from 'i18n';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { InvoiceDiscrepancy } from 'services/invoiceDiscrepancies/types';
import { InvoiceVerificationDTO, MessageV2DTO } from 'support/types';
import { Button } from 'components/Form/Button';

import { EmailModal } from './components/EmailModal';
import { getEmailLanguageForPartner, setEmailLanguageForPartner } from './emailLanguageByPartner';

export const GenerateDiscrepancyEmail = ({
  invoice,
  invoiceVerification,
}: {
  invoice: MessageV2DTO;
  invoiceVerification: InvoiceVerificationDTO;
}) => {
  // The dispute email is addressed to whoever sent the invoice — they are the ones who have to read
  // it, so the language is remembered against them rather than against the account writing it.
  const recipientPartnerId = invoice.sender_partner?.id;
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailLanguage, setEmailLanguage] = useState<AllowedLanguages>(() =>
    getEmailLanguageForPartner(recipientPartnerId),
  );
  const { service } = useNavigationContext<InvoiceDiscrepancy>();
  const numberOfErrors = service.useNumberOfActiveItems();
  const errors = service.useItems();
  const { t } = useTranslation();

  if (numberOfErrors === 0) {
    return null;
  }

  return (
    <>
      <Button
        variant="secondary"
        analyticsId="invoiceVerification:generateDiscrepancyEmail"
        LeftIcon={EnvelopeIcon}
        onClick={() => {
          setEmailLanguage(getEmailLanguageForPartner(recipientPartnerId));
          setEmailModalOpen(true);
        }}
      >
        {t('invoiceVerification:detailsPage.generateDiscrepancyEmail.cta')}
      </Button>
      <EmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        invoice={invoice}
        invoiceVerification={invoiceVerification}
        discrepancies={errors}
        language={emailLanguage}
        onLanguageChange={(language) => {
          setEmailLanguage(language);
          setEmailLanguageForPartner(recipientPartnerId, language);
        }}
      />
    </>
  );
};
